import Component from '@glimmer/component';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import Modal from 'scaleflow/components/modal';
import type { S3ObjectSummary } from 'scaleflow/utils/aws-clients/s3VDRClient';

export interface ManualUploadsDeleteModalSignature {
  Element: null;
  Args: {
    Named: {
      /** Archives awaiting confirmation; empty closes the modal. */
      archives: S3ObjectSummary[];
      onConfirm: () => void;
      onCancel: () => void;
      isDeleting?: boolean;
    };
  };
}

/**
 * Delete confirmation for a library archive.
 *
 * Follows `Goals::DeleteModal`'s structure but deliberately drops its
 * type-the-word-"delete" gate. That ceremony guards records other things
 * depend on; a library archive has no dependents, because picking one copies
 * it into the scan's own prefix — so deleting it cannot affect an X-Ray that
 * already uses it. The body says as much, since that is the question a user
 * would otherwise hesitate over.
 */
export default class ManualUploadsDeleteModal extends Component<ManualUploadsDeleteModalSignature> {
  get count(): number {
    return this.args.archives?.length ?? 0;
  }

  get onlyName(): string | undefined {
    return this.count === 1 ? this.args.archives[0]!.name : undefined;
  }

  @action
  handleToggle() {
    this.args.onCancel();
  }

  <template>
    {{#if this.count}}
      <Modal @isOpen={{true}} @toggleModal={{this.handleToggle}}>
        <:title>
          <h3 class="text-xl font-medium">
            {{t "manual-uploads.delete-title" count=this.count}}
          </h3>
        </:title>
        <:body>
          <p class="text-sm text-gray-700" data-test-delete-body>
            {{#if this.onlyName}}
              {{t "manual-uploads.delete-body" name=this.onlyName}}
            {{else}}
              {{t "manual-uploads.delete-body-many" count=this.count}}
            {{/if}}
          </p>
        </:body>
        <:footer>
          <button
            type="button"
            class="rounded-md px-3 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            data-test-confirm-delete
            disabled={{@isDeleting}}
            {{on "click" @onConfirm}}
          >
            {{t "manual-uploads.delete-confirm" count=this.count}}
          </button>
        </:footer>
      </Modal>
    {{/if}}
  </template>
}
